import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {isPlatformBrowser} from '@angular/common';
import {Observable, of, throwError} from 'rxjs';
import {catchError, finalize, map, shareReplay} from 'rxjs/operators';
import {ApiConstants} from '@coreConstant';
import {ServiceTokenService} from './service-token.service';

/**
 * Silent session renewal.
 *
 * The access token now lives ~15 minutes instead of 7 days, so an ordinary API call failing with
 * 401 is no longer "the session is over" — it is the routine, expected end of one access token
 * inside a session that is still perfectly valid. This service turns that 401 into a renewal.
 *
 * SINGLE-FLIGHT IS THE WHOLE POINT. Refresh tokens rotate: redeeming one consumes it and mints a
 * successor, and presenting a consumed token is how a stolen token is detected — it revokes the
 * entire session. A page that fires several requests at once (any product or quote screen does)
 * gets several simultaneous 401s, and if each of them called refresh independently the first would
 * succeed and the rest would look exactly like a replay attack. The user would be signed out for
 * loading a page.
 *
 * So every caller shares ONE in-flight refresh (`shareReplay`) and waits for its result. The server
 * keeps a short grace window as a second line of defence for the cases no client-side lock can
 * cover — a second browser tab, or a request already on the wire when the first refresh landed.
 */
@Injectable({providedIn: 'root'})
export class AuthRefreshService {

  private inFlight$: Observable<boolean> | null = null;

  constructor(
    private http: HttpClient,
    private serviceTokenService: ServiceTokenService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  /**
   * Renew the session, sharing one request across every concurrent caller.
   *
   * Resolves `true` when the cookies have been replaced and the caller should retry, `false` when
   * the session is genuinely over and the caller should fall through to its normal 401 handling.
   * It never throws: a refresh failure is an ordinary outcome, not an error to propagate.
   */
  refresh(): Observable<boolean> {
    // Never on the server. SSR and prerender have no cookie jar to renew, and a refresh attempt
    // during a build would just add a doomed request to every page render.
    if (!isPlatformBrowser(this.platformId)) {
      return of(false);
    }

    if (!this.inFlight$) {
      this.inFlight$ = this.http.post(ApiConstants.REFRESH_TOKEN_URL, {}).pipe(
        // The cached bearer token was minted from the OLD access token, so it is now stale. Drop it
        // rather than waiting for a cross-host call to fail on it. It is re-fetched on demand.
        // `expires_in` is the new cookie's lifetime, which schedules the next early renewal.
        map((res: any) => {
          this.serviceTokenService.renewed(res?.data?.expires_in ?? null);
          return true;
        }),
        catchError(() => of(false)),
        finalize(() => { this.inFlight$ = null; }),
        shareReplay(1),
      );
    }

    return this.inFlight$;
  }
}
